import { Helmet } from "react-helmet-async";

const SITE_URL = "https://bitgenix.io";
const SITE_NAME = "BitGenix";
const SITE_TIMEZONE = "+05:30";
const DEFAULT_OG_IMAGE = "/og-image.png";
const DEFAULT_IMAGE_ALT = "BitGenix crypto consultancy for tax, compliance, investment advisory, and blockchain development";
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const DEFAULT_AUTHOR = "BitGenix";
const BLOG_FEED_URL = `${SITE_URL}/feed.xml`;
const DEFAULT_KEYWORDS = [
  "crypto consultancy India",
  "crypto advisory India",
  "crypto tax consultant India",
  "Section 115BBH",
  "Section 194S",
  "FIU-IND registration consultant",
  "PMLA compliance crypto",
  "blockchain development consultancy",
  "NRI crypto tax advisory",
  "crypto portfolio advisory",
];

function formatKeywords(keywords = []) {
  return [...new Set([...DEFAULT_KEYWORDS, ...keywords].filter(Boolean))].join(", ");
}

function formatSchemaDate(value) {
  if (!value) return undefined;
  const parsed = new Date(`${value} UTC`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString().slice(0, 10);
}

function formatSchemaDateTime(value) {
  const safeDate = formatSchemaDate(value);
  if (!safeDate) return undefined;
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value;
  return `${safeDate}T00:00:00${SITE_TIMEZONE}`;
}

function getImageType(imageUrl) {
  if (imageUrl.endsWith(".png")) return "image/png";
  if (imageUrl.endsWith(".jpg") || imageUrl.endsWith(".jpeg")) return "image/jpeg";
  if (imageUrl.endsWith(".webp")) return "image/webp";
  if (imageUrl.endsWith(".svg")) return "image/svg+xml";
  return undefined;
}

function getArticleText(post) {
  return (post.content || [])
    .flatMap((block) => {
      if (block.type === "faq") {
        return block.items.flatMap((item) => [item.q, item.a]);
      }
      if (typeof block.text === "string") {
        return block.text;
      }
      return [];
    })
    .join(" ")
    .trim();
}

function getWordCount(text) {
  if (!text) return undefined;
  return text.split(/\s+/).filter(Boolean).length;
}

export default function SEO({
  title,
  description,
  path = "",
  type = "website",
  article = null,
  jsonLd = null,
  keywords = [],
  robots = DEFAULT_ROBOTS,
  image = DEFAULT_OG_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  author = DEFAULT_AUTHOR,
  includeFeed = false,
}) {
  const includesBrand = title?.toLowerCase().includes(SITE_NAME.toLowerCase());
  const fullTitle = title
    ? (includesBrand ? title : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} | India's Trusted Crypto Consultancy`;
  const url = `${SITE_URL}${path}`;
  const imageUrl = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const imageType = getImageType(imageUrl);
  const metaKeywords = formatKeywords(keywords);
  const publishedTime = article ? formatSchemaDateTime(article.date) : undefined;
  const modifiedTime = article ? formatSchemaDateTime(article.modifiedDate || article.date) : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en-IN" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      {includeFeed && <link rel="alternate" type="application/rss+xml" title={`${SITE_NAME} Blog Feed`} href={BLOG_FEED_URL} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt} />
      {imageType && <meta property="og:image:type" content={imageType} />}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />
      {modifiedTime && <meta property="og:updated_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Article-specific */}
      {article && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {article && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {article && <meta property="article:author" content={article.author} />}
      {article && <meta property="article:publisher" content={SITE_NAME} />}
      {article && article.section && <meta property="article:section" content={article.section} />}
      {article && article.tags?.length > 0 && <meta name="news_keywords" content={article.tags.join(", ")} />}
      {article && article.tags && article.tags.map((tag, i) => (
        <meta key={i} property="article:tag" content={tag} />
      ))}

      {/* JSON-LD */}
      {jsonLd && !Array.isArray(jsonLd) && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      {jsonLd && Array.isArray(jsonLd) && jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "BitGenix",
    description: "End-to-end crypto consultancy for individuals and businesses. Investment advisory, crypto tax planning, regulatory compliance, blockchain development.",
    url: SITE_URL,
    logo: `${SITE_URL}/logo-navy.svg`,
    email: "hello@bitgenix.io",
    knowsAbout: [
      "Crypto tax planning in India",
      "Section 115BBH compliance",
      "Section 194S TDS planning",
      "FIU-IND registration",
      "PMLA compliance",
      "Crypto investment advisory",
      "Blockchain development",
      "NRI crypto tax advisory",
      "Tokenization consulting",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      email: "hello@bitgenix.io",
      contactType: "customer service",
      availableLanguage: ["English", "Hindi"]
    },
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Global" }
    ]
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "en-IN",
    description: "BitGenix helps Indian and global clients with crypto tax, compliance, investment advisory, and blockchain consulting.",
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    keywords: DEFAULT_KEYWORDS.join(", "),
  };
}

export function serviceSchema(service, isIndividual) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.heroDesc,
    provider: {
      "@type": "Organization",
      name: "BitGenix",
      url: SITE_URL
    },
    serviceType: isIndividual ? "Individual Crypto Advisory" : "Business Crypto Advisory",
    areaServed: { "@type": "Country", name: "India" },
    url: `${SITE_URL}/services/${service.slug}`
  };
}

export function faqSchema(faqItems) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(item => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a
      }
    }))
  };
}

export function blogPostSchema(post) {
  const articleText = getArticleText(post);
  const publishedDate = formatSchemaDateTime(post.date);
  const modifiedDate = formatSchemaDateTime(post.modifiedDate || post.date);

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: `${SITE_URL}${DEFAULT_OG_IMAGE}`,
    author: {
      "@type": "Organization",
      name: post.author || "BitGenix"
    },
    publisher: {
      "@type": "Organization",
      name: "BitGenix",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-mark-navy.svg` }
    },
    datePublished: publishedDate,
    dateModified: modifiedDate,
    articleSection: post.category,
    inLanguage: "en-IN",
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
    url: `${SITE_URL}/blog/${post.slug}`,
    keywords: post.tags ? post.tags.join(", ") : "",
    wordCount: getWordCount(articleText),
    isAccessibleForFree: true,
    isPartOf: {
      "@type": "Blog",
      name: `${SITE_NAME} Insights`,
      url: `${SITE_URL}/blog`,
    },
    about: (post.tags || []).map((tag) => ({
      "@type": "Thing",
      name: tag,
    })),
  };
}

export function calculatorSchema(calc) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: calc.title,
    description: calc.seoDesc,
    url: `${SITE_URL}/calculators/${calc.slug}`,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    isAccessibleForFree: true,
    inLanguage: "en-IN",
    keywords: calc.tags ? calc.tags.join(", ") : "",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR"
    },
    provider: {
      "@type": "Organization",
      name: "BitGenix",
      url: SITE_URL
    }
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`
    }))
  };
}

export function aboutPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About BitGenix",
    url: `${SITE_URL}/about`,
    description: "Learn about BitGenix, India's trusted crypto consultancy for tax, compliance, investment advisory, and blockchain development.",
    about: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    inLanguage: "en-IN",
  };
}

export function contactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact BitGenix",
    url: `${SITE_URL}/contact`,
    description: "Book a free consultation with BitGenix for crypto tax planning, compliance, investment advisory, and blockchain consulting.",
    about: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      email: "hello@bitgenix.io",
    },
    inLanguage: "en-IN",
  };
}
